import { NavLink, useParams, useNavigate, Link } from "react-router";
import React, { useEffect } from "react";
import { Alert, Form, Input, Typography, App, Button } from "antd";
import { Error as CommonError } from "@triare/auth-redux/dist/saga/common";
import { Action } from "@triare/auth-redux";
import { connect } from "react-redux";
import {
  resetPassword as resetPasswordAction,
  resetPasswordErrorClear as resetPasswordErrorClearAction,
  ResetPasswordAction,
  ResetPasswordResponse,
} from "@triare/auth-redux/dist/saga/auth/resetPassword";
import {
  validationNewPassword,
  createRulesForAntd,
} from "../../../../utils/validations";
import { getMessageInError } from "../../../../hooks/fetch";
import Wrapper from "../Wrapper";
import Submit from "../../../Common/Form/Button/Submit";
import { RootState } from "../../../../store";
import { moduleName } from "../../../../store/auth";
import { useTranslation } from "react-i18next";

import styles from "./index.module.scss";

const validationRules = createRulesForAntd(validationNewPassword);

interface ResetPasswordProps {
  loading: boolean;
  response: ResetPasswordResponse | null;
  error: CommonError;
  resetPassword: (payload: ResetPasswordAction) => Action;
  resetPasswordErrorClear: () => Action;
}

function ResetPassword({
  resetPassword,
  resetPasswordErrorClear,
  loading,
  response,
  error,
}: ResetPasswordProps) {
  const { secretKey } = useParams();
  const { message } = App.useApp();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const onFinish = ({ password }: { password: string }) => {
    if (secretKey) {
      resetPassword({ password, secretKey });
    }
  };

  useEffect(() => {
    if (response) {
      message.info("Your password has been successfully changed");
      navigate("/sign-in");
    }
  }, [response]);

  return (
    <Wrapper>
      <Form className={styles.form} layout="vertical" onFinish={onFinish}>
        <Typography.Title level={5} className={styles.title}>
          Set and confirm new password
        </Typography.Title>

        {error && (
          <Form.Item>
            <Alert
              type="error"
              onClose={() => resetPasswordErrorClear()}
              message={getMessageInError(error)}
              closable
              showIcon
            />
          </Form.Item>
        )}

        <Form.Item
          name="password"
          label={`${t("newPassword")} *`}
          className={styles.input}
          rules={[validationRules]}
        >
          <Input.Password placeholder={t("newPassword")} size="large" />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label={`${t("confirmNewPassword")} *`}
          className={styles.input}
          dependencies={["password"]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }

                return Promise.reject(
                  new Error(t("validations.passwordsDoNotMatch")),
                  // "Passwords that you entered do not match!"
                );
              },
            }),
          ]}
        >
          <Input.Password placeholder="Confirm new password" size="large" />
        </Form.Item>

        <Submit loading={loading} className={styles.btn} size="large">
          Reset password
        </Submit>
        <Form.Item>
          <Link to="/sign-in">
            <Button
              type="link"
              size="large"
              // style={{ color: "#8C8C8C" }}
              block
            >
              Back to Login
            </Button>
          </Link>
        </Form.Item>
      </Form>
    </Wrapper>
  );
}

export default connect(
  (state: RootState) => ({
    loading: state[moduleName].resetPassword.loading,
    response: state[moduleName].resetPassword.response,
    error: state[moduleName].resetPassword.error,
  }),
  {
    resetPassword: resetPasswordAction,
    resetPasswordErrorClear: resetPasswordErrorClearAction,
  },
)(ResetPassword);
